import { cn } from "@/lib/utils";
import type { DiffSpan } from "./word-diff";

/**
 * A word-level diff, rendered for reading.
 *
 * Additions and removals are marked with colour *and* with strikethrough, so the
 * two are still distinguishable to a reader who cannot separate red from green.
 *
 * `whitespace-pre-wrap` matters: the diff carries the original whitespace, and
 * collapsing it would show a paragraph reflow as a change to every line.
 */

interface DiffViewProps {
  spans: DiffSpan[];
  /**
   * False when the passage was too long to diff word by word — the spans are
   * then the whole before and the whole after, which needs saying.
   */
  wordLevel?: boolean;
  className?: string;
}

export function DiffView({ spans, wordLevel = true, className }: DiffViewProps) {
  if (!spans.length) {
    return (
      <p className={cn("text-xs italic text-muted-foreground", className)}>
        The selection is empty.
      </p>
    );
  }

  return (
    <div className={className}>
      {!wordLevel && (
        <p className="mb-2 text-[11px] text-muted-foreground">
          This selection is too long to compare word by word, so the old and new
          versions are shown in full.
        </p>
      )}
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
        {spans.map((span, index) => {
          if (span.kind === "same") {
            return <span key={index}>{span.text}</span>;
          }
          if (span.kind === "added") {
            return (
              <span
                key={index}
                className="rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                // Marked up as an insertion, so it is not colour alone.
                data-diff="added"
              >
                {span.text}
              </span>
            );
          }
          return (
            <span
              key={index}
              className="rounded bg-red-500/15 text-red-700 line-through dark:text-red-400"
              data-diff="removed"
            >
              {span.text}
            </span>
          );
        })}
      </p>
    </div>
  );
}
